<script setup lang="ts">

import { ref } from 'vue';
import { Trash2, AlertTriangle, X } from 'lucide-vue-next';
import Button from '../../button/Button.vue';

interface Props {
  isFirstSection: boolean;
  isLastSection: boolean;
  isValidating?: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onSubmit: () => void;
  onClearAll: () => void;
}

const props = defineProps<Props>();

const showDeleteModal = ref(false);

const handlePrevious = () => {
  if (!props.isFirstSection) {
    props.onPrevious();
  }
};

const handleNext = () => {
  // Correction : Permettre la navigation tant qu'on n'est pas à la dernière section
  if (!props.isValidating) {
    props.onNext();
  }
};

const handleSubmit = () => {
  // Correction : Vérifier si on peut soumettre
  if (!props.isValidating) {
    props.onSubmit();
  }
};

const handleClearAll = () => {
  props.onClearAll();
  showDeleteModal.value = false;
};
</script>

<template>
  <div class="md:flex justify-between items-center gap-4 mt-8 w-full max-w-[1080px]">
    <!-- Version Desktop -->
    <div class="hidden md:flex justify-between items-center gap-2 mt-8 w-full max-w-[1080px]">
      <Button :disabled="props.isFirstSection" @click="handlePrevious" class="bg-[#888383] text-[#04406E]" >
        Précédent
      </Button>

      <div class="flex-1 flex justify-center">
        <button type="button"
          class="group relative p-3 bg-red-600 text-white rounded-full hover:bg-red-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
          title="Supprimer toutes les données" @click="showDeleteModal = true">
          <Trash2 :size="20" class="transition-transform group-hover:scale-110" />
        </button>
      </div>

      <Button v-if="!props.isLastSection" variant="validation" :disabled="props.isValidating" @click="handleNext">
        {{ props.isValidating ? 'Validation...' : 'Suivant' }}
      </Button>

      <Button v-else variant="validation" :disabled="props.isValidating" @click="handleSubmit">
        {{ props.isValidating ? 'Validation...' : 'Envoyer' }}
      </Button>
    </div>

    <!-- Version Mobile -->
    <div
      class="md:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-gray-200 px-4 py-3 shadow-lg z-50 safe-area-bottom">
      <div class="flex justify-between items-center gap-3 max-w-md mx-auto">
        <Button variant="cancel" :disabled="props.isFirstSection" @click="handlePrevious">
          Précédent
        </Button>

        <button type="button"
          class="group relative p-3 bg-red-600 text-white rounded-full hover:bg-red-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 min-h-[44px] min-w-[44px] flex items-center justify-center"
          title="Supprimer toutes les données" @click="showDeleteModal = true">
          <Trash2 :size="20" class="transition-transform group-hover:scale-110" />
        </button>

        <Button v-if="!props.isLastSection" variant="validation" :disabled="props.isValidating" @click="handleNext">
          {{ props.isValidating ? 'Validation...' : 'Suivant' }}
        </Button>
        <Button v-else variant="validation" :disabled="props.isValidating" @click="handleSubmit">
          {{ props.isValidating ? 'Validation...' : 'Envoyer' }}
        </Button>
      </div>

      <div class="h-safe-area-inset-bottom"></div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal"
      class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 relative z-[10000]">
        <!-- Header -->
        <div class="flex items-center justify-between p-6 border-b border-gray-200">
          <div class="flex items-center space-x-3">
            <AlertTriangle class="h-6 w-6 text-red-600" />
            <h3 class="text-lg font-semibold text-gray-900">
              Supprimer toutes les données
            </h3>
          </div>
          <button type="button" class="text-gray-400 hover:text-gray-600 transition-colors"
            @click="showDeleteModal = false">
            <X :size="24" />
          </button>
        </div>

        <!-- Body -->
        <div class="p-6">
          <p class="text-gray-600 mb-4">
            Êtes-vous sûr de vouloir supprimer toutes les données du formulaire ?
            Cette action est irréversible et effacera également les données sauvegardées.
          </p>
          <div class="bg-red-50 border border-red-200 rounded-md p-3 flex">
            <AlertTriangle class="h-5 w-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
            <div class="text-sm text-red-800">
              <strong>Attention :</strong> Toutes vos données seront définitivement perdues.
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button type="button"
            class="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition"
            @click="showDeleteModal = false">
            Annuler
          </button>
          <button type="button"
            class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition flex items-center space-x-2"
            @click="handleClearAll">
            <Trash2 :size="16" />
            <span>Supprimer</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>